import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { AnalysisUseCase, useListAnalysesQuery } from 'generated/graphql';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useFetchMore } from 'v2/core/repository/edge-server/utils/records';
import { useGetUser } from 'v2/core/services/user';

interface UseFetchCollectionsForFeedbackRecordProps {
  searchTerm?: string;
}

export const useFetchCollectionsForFeedbackRecord = ({
  searchTerm,
}: UseFetchCollectionsForFeedbackRecordProps) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();

  const {
    data,
    error,
    loading,
    called,
    fetchMore: analysisFetchMore,
  } = useListAnalysesQuery({
    variables: {
      request: {
        userID,
        tenantID,
        filter: {
          useCase: AnalysisUseCase.Collection,
          createdBy: userID,
          query: searchTerm || undefined,
        },
      },
    },
    fetchPolicy: 'no-cache',
    onError: () => {
      showErrorNotification({
        contentComponent: 'Failed to fetch Collections',
        props: {},
      });
    },
  });

  const analyses = data?.listAnalyses;

  const [isLoadingMore, fetchMore] = useFetchMore(
    analysisFetchMore,
    data?.listAnalyses?.nextCursor,
  );

  const analysisStatus = apolloStatusToQueryStatus(
    loading,
    called,
    false,
    error,
  );

  return {
    collections:
      analyses?.analyses.map((analysis) => ({
        id: analysis.id,
        title: analysis.title,
        editToken: analysis.editToken,
        viewToken: analysis.viewToken,
        eqlExpression: analysis.context?.eqlExpression ?? '',
        savedFeedbackMetadata:
          analysis.metadataV2?.collection?.savedFeedbackMetadata,
      })) ?? [],
    isLoading: IsLoading(analysisStatus) || isLoadingMore,
    fetchMore,
    hasMore: !!data?.listAnalyses?.nextCursor,
  } as const;
};
